import { useLayoutEffect, useRef } from 'react';

enum TransitionAxis {
  x = 'x',
  y = 'y',
}

type TransitionConfig = {
  transitionDuration?: `${number}ms` | `${number}s`;
  transitionAxes?: TransitionAxis[];
};

/**
 * Adds animated position transitions when React rerenders result in changes
 * to an element's bounding box. Useful for animating list reordering.
 */
export const useAnimatedPositionTransition = <E extends HTMLElement>({
  transitionDuration = '300ms',
  transitionAxes = [TransitionAxis.y],
}: TransitionConfig = {}) => {
  const ref = useRef<E | null>(null);
  const prevY = useRef<number | null>(null);
  const prevX = useRef<number | null>(null);

  const shouldTrackX = transitionAxes.includes(TransitionAxis.x);
  const shouldTrackY = transitionAxes.includes(TransitionAxis.y);

  useLayoutEffect(() => {
    if (!ref.current) return;

    const currentX = ref.current.getBoundingClientRect().x + window.scrollX;
    const currentY = ref.current.getBoundingClientRect().y + window.scrollY;

    const shouldTransitionX = shouldTrackX && currentX !== prevX.current;
    const shouldTransitionY = shouldTrackY && currentY !== prevY.current;

    // FLIP-style JS/CSS transition: https://css-tricks.com/everything-you-need-to-know-about-flip-animations-in-react/
    const translateX =
      shouldTransitionX && prevX.current !== null
        ? `translateX(${prevX.current - currentX}px)`
        : null;
    const translateY =
      shouldTransitionY && prevY.current !== null
        ? `translateY(${prevY.current - currentY}px)`
        : null;
    const transforms = [translateX, translateY].filter((t) => !!t);

    if (transforms.length) {
      ref.current.style.transition = 'transform 0s';
      ref.current.style.transform = transforms.join(' ');

      // Double RAF required for certain browsers
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (ref.current) {
            ref.current.style.transform = '';
            ref.current.style.transition = `transform ${transitionDuration} ease`;
          }
        });
      });
    }
    prevX.current = currentX;
    prevY.current = currentY;
  });

  return ref;
};
